import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Switch,
  Button,
} from "@mui/material";
import axios from "axios";

// ✅ Default permissions (matrix)
const PERMISSION_MATRIX = {
  CUSTOMER: {
    createCustomer: 1,
    createShop: 0,
    createEmployee: 0,
    createDeliveryBoy: 0,
    masterInventory: 0,
    crudInventory: 0,
    salesReport: 0,
    cartAction: 1,
    orderInventory: 1,
    trackOrderInventory: 1,
    bulkImportExport: 0,
  },
  SHOPKEEPER: {
    createCustomer: 0,
    createShop: 0,
    createEmployee: 1,
    createDeliveryBoy: 1,
    masterInventory: 1,
    crudInventory: 1,
    salesReport: 1,
    cartAction: 1,
    orderInventory: 1,
    trackOrderInventory: 0,
    bulkImportExport: 1,
  },
  EMPLOYEE: {
    createCustomer: 0,
    createShop: 0,
    createEmployee: 0,
    createDeliveryBoy: 0,
    masterInventory: 0,
    crudInventory: 1,
    salesReport: 0,
    cartAction: 1,
    orderInventory: 1,
    trackOrderInventory: 1,
    bulkImportExport: 0,
  },
  DB: {
    createCustomer: 0,
    createShop: 0,
    createEmployee: 0,
    createDeliveryBoy: 0,
    masterInventory: 0,
    crudInventory: 0,
    salesReport: 0,
    cartAction: 0,
    orderInventory: 0,
    trackOrderInventory: 0,
    bulkImportExport: 0,
  },
  RM: {
    createCustomer: 0,
    createShop: 1,
    createEmployee: 0,
    createDeliveryBoy: 0,
    masterInventory: 0,
    crudInventory: 0,
    salesReport: 0,
    cartAction: 0,
    orderInventory: 0,
    trackOrderInventory: 0,
    bulkImportExport: 1,
  },
  ADMIN: {
    createCustomer: 0,
    createShop: 1,
    createEmployee: 0,
    createDeliveryBoy: 0,
    masterInventory: 1,
    crudInventory: 1,
    salesReport: 1,
    cartAction: 1,
    orderInventory: 1,
    trackOrderInventory: 1,
    bulkImportExport: 1,
  },
};

const ACTIONS = Object.keys(PERMISSION_MATRIX.CUSTOMER);

const PermissionPage = () => {
  const [permissions, setPermissions] = useState(PERMISSION_MATRIX);

  // ✅ Toggle handler
  const handleToggle = (role, action) => {
    if (PERMISSION_MATRIX[role][action] === 0) return; // default lock

    const updated = {
      ...permissions,
      [role]: {
        ...permissions[role],
        [action]: permissions[role][action] === 1 ? 0 : 1,
      },
    };

    setPermissions(updated);

    // ✅ Update to LIVE backend
    axios
      .put(`https://sakhisshop.com/api/permissions/${role}/${action}`, {
        enabled: updated[role][action] === 1,
      })
      .then(() => console.log("✅ Updated in LIVE DB:", role, action))
      .catch((err) => console.error("❌ Update failed:", err));
  };

  // ✅ Sync all permissions to backend
  const syncPermissionsToBackend = async () => {
    try {
      await axios.post(
        "https://sakhisshop.com/api/permissions/bulk-update",
        permissions,
      );
      console.log("✅ All permissions synced to LIVE DB");
    } catch (err) {
      console.error("❌ Sync failed:", err);
    }
  };

  // 👇 Add one more function to reset
  const resetToDefault = () => {
    setPermissions(PERMISSION_MATRIX); // reset state back to default
    console.log("🔄 Permissions reset to default");

    // optional: also sync reset with backend
    axios
      .post(
        "https://sakhisshop.com/api/permissions/bulk-update",
        PERMISSION_MATRIX,
      )
      .then(() => console.log("✅ Reset synced to LIVE DB"))
      .catch((err) => console.error("❌ Reset sync failed:", err));
  };

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Role Permissions
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              {ACTIONS.map((action) => (
                <TableCell key={action}>{action}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(permissions).map((role) => (
              <TableRow key={role}>
                <TableCell>{role}</TableCell>
                {ACTIONS.map((action) => (
                  <TableCell key={action}>
                    <Switch
                      size="small"
                      checked={permissions[role][action] === 1}
                      onChange={() => handleToggle(role, action)}
                      disabled={PERMISSION_MATRIX[role][action] === 0} // default 0 → lock
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ✅ Sync button */}
      {/* ✅ Action buttons */}
      <Box mt={2} display="flex" justifyContent="flex-start" gap={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={resetToDefault}
          sx={{ borderRadius: "8px" }}
        >
          Reset to Default
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={syncPermissionsToBackend}
          sx={{ borderRadius: "8px" }}
        >
          Save All Permissions
        </Button>
      </Box>
    </Box>
  );
};

export default PermissionPage;
